import { ColDef, RowClickedEvent, RowDoubleClickedEvent } from "ag-grid-community";
import { AgGridReact } from "ag-grid-react";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { aggridHeaderClass } from "src/components/common/common";
import { useGridSettingContent } from "src/utils/grid/GridSettingContent";
import { createSaveColumnState, defaultColDef, getDefaultRowIndexColumnConfig, getRowStyle, onGridReady } from "src/utils/grid/gridStyleUtils";
import { formatDate } from "src/utils/string/stringUtil";
import { usePUR1020_R01Store } from "./Store_PUR1020_R01";
import { gridRefSelectedRowHandler } from "src/utils/grid/GridHandlerContent";

import { defaultPUR1020_R01HeaderDto, PUR1020_R01HeaderDto } from "./Dto_PUR1020_R01";
import { GridCustomColumnDefs } from "src/utils/grid/GridCustomColumnDefs";
import { buildDefaultColumnStateFromDefs } from "src/utils/grid/columnPinUtils";
import { GridContextWrapper } from "src/components/common/gridWrapper/GridContextWrapper";
import Grid_Touch from "src/components/common/gridWrapper/Grid_Touch";
import { onlyTouchGridStyle } from "src/utils/grid/gridFunctionUtil";
import { cellDateComparator } from "src/utils/grid/gridFunctionUtil";

const COLUMN_STATE_KEY = 'PUR1020_R01-grid-column-state';
// 숫자 컬럼 목록 (헤더 DTO 키 보장 필요 없으므로 문자열 배열로 유지)
const numberFields: string[] = ['salAmt'];

interface Props {
    gridRef: React.RefObject<AgGridReact<PUR1020_R01HeaderDto> | null>;
	handleCreateClick?: () => void;
}

export const Table_PUR1020_R01 = (props: Props) => {

	const { gridRef, handleCreateClick } = props;

    const {
		PUR1020_R01List, 
        selectedPUR1020_R01, setSelectedPUR1020_R01, initSelectedPUR1020_R01,
		columnState, setColumnState, 
        PUR1020_R01PurchaseList, setPUR1020_R01PurchaseList,
        PUR1020_R01SuppliedList, setPUR1020_R01SuppliedList,
		setSelectedPUR1020_R01PurchaseList, setSelectedPUR1020_R01SuppliedList,
		scrollTop, setScrollTop,
		setPurchaseScrollTop, setSuppliedScrollTop,
    } = usePUR1020_R01Store() as any;


	//const gridRef = useRef<AgGridReact>(null);
	const [hoveredRowIndex, setHoveredRowIndex] = useState<number | null>(null);

	const { 
		getRowId,
		settingRowIndex, 
		settingSecondColumnCount,
		createRowClassRules,
		paddedGridData,
		renderIndexCell,
		defaultRowIndexCellButton,
		rowIndexCellButtonType1,
		rowIndexCellButtonType2,
		ensureSelectedRow,
    } = useGridSettingContent<PUR1020_R01HeaderDto>({
		gridRef,
		key: 'salesOrderItemNo',
        gridData: PUR1020_R01List,
        defaultGridData: defaultPUR1020_R01HeaderDto,
        selectedRow: selectedPUR1020_R01,
	});

	const headerNames: Record<string, string> = {
		rowIndex: "",
		orderDt: '수주일자',
		customNm: '거래처',
		itemCd: '품목코드',
		itemNm: '품명',
		orderQty: '주문량',
	}

	const getCellStyle = useCallback((params: unknown, key: string) => onlyTouchGridStyle(key, 'compCd')(params), []);

    const baseColumnDefs: ColDef<PUR1020_R01HeaderDto>[] = useMemo(() => [
		...getDefaultRowIndexColumnConfig(),
		{
			headerName: '수주일자', colId: 'orderDt', field: 'orderDt', width: 120,
			cellStyle: (p) => getCellStyle(p, 'orderDt'),
			headerClass: aggridHeaderClass,
			valueFormatter: (p: { data?: PUR1020_R01HeaderDto }) =>
				p.data?.orderDt ? formatDate(new Date(p.data.orderDt)) : '',
			comparator: cellDateComparator,
		},
		{
			headerName: '거래처명', colId: 'customNm', field: 'customNm', width: 180,
			cellStyle: (p) => getCellStyle(p, 'customNm'),
			headerClass: aggridHeaderClass,
		},
		{
			headerName: '품목코드', colId: 'itemCd', field: 'itemCd', width: 180,
			cellStyle: (p) => getCellStyle(p, 'itemCd'),
			headerClass: aggridHeaderClass,
		},
		{
			headerName: '품명', colId: 'itemNm', field: 'itemNm', width: 180,
			cellStyle: (p) => getCellStyle(p, 'customNm'),
			headerClass: aggridHeaderClass,
		},
		{
			headerName: '주문량', colId: 'orderQty', field: 'orderQty', width: 90,
			cellStyle: (p) => getCellStyle(p, 'orderQty'),
			headerClass: aggridHeaderClass,
			valueFormatter: (params) => params.value?.toLocaleString() ?? '0',
		},
    ], [getCellStyle, cellDateComparator]);

	const defaultColumnState = useMemo(
		() => buildDefaultColumnStateFromDefs(baseColumnDefs, [])
	, [baseColumnDefs]);

    const columnDefs: ColDef<PUR1020_R01HeaderDto>[] = useMemo(() => {
		if (columnState === null) {
			return baseColumnDefs;
		}
        return GridCustomColumnDefs<PUR1020_R01HeaderDto>({
			baseColumnDefs,
			columnState,
			headerNames,
			fallbackHeaderClass: aggridHeaderClass,
		});
	}, [columnState, settingRowIndex, baseColumnDefs, headerNames]);


	// 마운트 시 컬럼 상태 복원
	
    const handleRowClick = useCallback((e: RowClickedEvent<PUR1020_R01HeaderDto>) => {
		if (e.node.rowPinned === 'bottom') return;
		const rowData = e.data;
		if (!rowData) return;
		
		// 선택된 표와 정보가 같은지 확인 - salesOrderItemNo 사용
		const currentKey = rowData.salesOrderItemNo;
        const selectedKey = selectedPUR1020_R01?.salesOrderItemNo;
		const isSelected = selectedKey === currentKey;
		
		if (isSelected) {
            setSelectedPUR1020_R01(undefined);
			setPurchaseScrollTop(null);
			setSuppliedScrollTop(null);
		} else {
			setPurchaseScrollTop(null);
			setSuppliedScrollTop(null);
            setSelectedPUR1020_R01(rowData);
		}
    }, [selectedPUR1020_R01, setSelectedPUR1020_R01, setPurchaseScrollTop, setSuppliedScrollTop]);

	
    const pinnedBottomRow = useMemo(() => ([{
		rowIndex: '',
        compCd: PUR1020_R01List.length.toLocaleString() + "건",
		orderQty: PUR1020_R01List.reduce((sum: number, item: PUR1020_R01HeaderDto) => sum + (Number(item.orderQty) || 0), 0),
    }]), [PUR1020_R01List]);

	useEffect(() => { ensureSelectedRow(); }, [PUR1020_R01List, selectedPUR1020_R01]);

	return (
		
        <GridContextWrapper<PUR1020_R01HeaderDto>
        	enableCellCalculation
			gridRef={gridRef}
			paddedGridData={paddedGridData}
            initSelected={initSelectedPUR1020_R01}
			defaultRowData={defaultPUR1020_R01HeaderDto}
			baseColumnDefs={columnState ? columnDefs : baseColumnDefs}
			defaultColumnState={defaultColumnState}
			setColumnState={setColumnState}
			localStorageKey={COLUMN_STATE_KEY}
			menuItems={undefined}
		>
            <Grid_Touch<PUR1020_R01HeaderDto>
				ref={gridRef}
				rowData={paddedGridData}
				columnDefs={columnDefs}
				defaultColDef={defaultColDef}
				getRowId={getRowId}
				
                rowClassRules={createRowClassRules<PUR1020_R01HeaderDto>({
					idKey: 'salesOrderItemNo',
                    selectedList: selectedPUR1020_R01?.salesOrderItemNo ? [selectedPUR1020_R01.salesOrderItemNo] : [],
					deletedList: [],
					
				})}
				onRowClicked={handleRowClick}
				onCellKeyDown={undefined}
				pinnedBottomRowData={pinnedBottomRow}	
				
				// Grid_Select 공통 로직 처리를 위한 props 전달
				setHoveredRowIndex={setHoveredRowIndex}		//마우스 오버 상태 이벤트를 위한 핸들러
				columnStateKey={COLUMN_STATE_KEY}			//컬럼 상태 (로컬스토리)	
				setColumnState={setColumnState}			//컬럼 상태 저장
				scrollTop={scrollTop}
				setScrollTop={setScrollTop}
			/>
		</GridContextWrapper>
	)
}
